// Copyright 2026 andrewzeck
// 
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
// 
//     https://www.apache.org/licenses/LICENSE-2.0
// 
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, {useEffect, useState} from 'react';
import TabbedNavigation from "@/components/TabbedNavigation.jsx";
import CommissionsTable from "./CommissionsTable.jsx";
import {getPlans, createPlan, getCommissionForSeller, getActiveCommissions, getPastCommissions, applyCommissionToSeller, deactivateCommissionForSeller} from "../../api/commissions.js";

const MarketplaceCommissions = () => {
  const [selectedTab, setSelectedTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [activeCommissions, setActiveCommissions] = useState([]);
  const [pastCommissions, setPastCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansData, activeCommissionsData, pastCommissionsData] = await Promise.all([
        getPlans(),
        getActiveCommissions(),
        getPastCommissions()
      ]);
      
      setPlans(plansData);
      setActiveCommissions(activeCommissionsData);
      setPastCommissions(pastCommissionsData);
    } catch(error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    
    const load = async () => {
      await loadData();
      if (cancelled) {
        // Don't update state if unmounted
        return;
      }
    };
    
    load();
    
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        <div className="flex items-center">
          <TabbedNavigation
            tabs={[
              { id: 'plans', label: 'Commission Templates' },
              { id: 'active-commissions', label: 'Active Commissions' },
              { id: 'past-commissions', label: 'Past Commissions' }
            ]}
            activeTab={selectedTab}
            onTabChange={(newTab) => {
              setSelectedTab(newTab);
            }}
            variant="default"
            size="md"
          />
        </div>
      </div>
      
      {/* Table renders below the header */}
      <CommissionsTable
        plans={plans}
        loading={loading}
        activeCommissions={activeCommissions}
        pastCommissions={pastCommissions}
        commissionType={selectedTab}
        onPlanChange={loadData}
      />
    </div>
  )
}

export default MarketplaceCommissions;